import { IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus } from '@assets/icons/32x32';
import { ButtonGroup, For, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import {
  OperatorStationConfigurationTabEnum,
  useOperatorStationConfigurationContext,
} from '@components/cockpit/panels/operatorStationConfiguration/OperatorStationConfigurationContext';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { GlobalScreenConfigurationSelectors } from '@redux/config/config.selectors';
import { useDeleteGlobalScreenConfigurationMutation } from '@services/config/globalScreenConfiguration.api';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { GlobalScreenConfiguration } from '@/types/config/screenConfiguration.types';

type GlobalScreenConfigurationsDialogProps = {
  name?: string;
} & Omit<CockpitDialogProps, 'children'>;

function GlobalScreenConfigurationsDialog({
  name,
  open,
  isLoading,
  onConfirm,
  onCancel,
}: Readonly<GlobalScreenConfigurationsDialogProps>) {
  return (
    <CockpitDialog open={open} isLoading={isLoading} onCancel={onCancel} onConfirm={onConfirm}>
      <Text color="neutral.100" width="100%">
        <FormattedMessage
          id="components.operatorStationConfiguration.context.popup.descriptionDelete"
          values={{ name }}
        />
      </Text>
    </CockpitDialog>
  );
}

type GlobalScreenConfigurationsActionsProps = {
  globalScreenConfigsLength: number;
  canCreateScreenConfig: boolean;
  onAction: () => void;
};

function GlobalScreenConfigurationsActions({
  globalScreenConfigsLength,
  canCreateScreenConfig,
  onAction,
}: Readonly<GlobalScreenConfigurationsActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage
          id="components.operatorStationConfiguration.context.result"
          values={{ count: globalScreenConfigsLength }}
        />
      </Text>
      {canCreateScreenConfig && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onAction}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

export function GlobalScreenConfigurations() {
  const { formatMessage } = useIntl();

  const { setEditedItem } = useOperatorStationConfigurationContext();
  const globalScreenConfigs = useAppSelector(GlobalScreenConfigurationSelectors.selectAllGlobalScreenConfigurations);

  const canEditScreenConfig = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_GLOBAL_SCREEN_CONFIGURATION));
  const canCreateScreenConfig = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_GLOBAL_SCREEN_CONFIGURATION));
  const canDeleteScreenConfig = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_GLOBAL_SCREEN_CONFIGURATION));

  const [deleteScreenConfiguration, { isLoading }] = useDeleteGlobalScreenConfigurationMutation();

  const [selectedScreenConfiguration, setSelectedScreenConfiguration] = useState<GlobalScreenConfiguration | null>(
    null,
  );

  return (
    <CockpitListLayout
      dialog={
        <GlobalScreenConfigurationsDialog
          open={selectedScreenConfiguration !== null}
          name={selectedScreenConfiguration?.name}
          isLoading={isLoading}
          onCancel={() => setSelectedScreenConfiguration(null)}
          onConfirm={() => {
            if (!selectedScreenConfiguration) {
              return;
            }
            deleteScreenConfiguration(selectedScreenConfiguration.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage(
                    { id: 'components.operatorStationConfiguration.context.message.delete' },
                    { name: selectedScreenConfiguration.name },
                  ),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedScreenConfiguration(null);
              });
          }}
        />
      }
      actions={
        <GlobalScreenConfigurationsActions
          globalScreenConfigsLength={globalScreenConfigs.length}
          canCreateScreenConfig={canCreateScreenConfig}
          onAction={() => setEditedItem({ tab: OperatorStationConfigurationTabEnum.CONTEXT, id: null })}
        />
      }
    >
      <For
        each={globalScreenConfigs.toSorted((a, b) => a.name.localeCompare(b.name))}
        fallback={
          canCreateScreenConfig ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ tab: OperatorStationConfigurationTabEnum.CONTEXT, id: null })}
            >
              <Icon as={IcPlus} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.operatorStationConfiguration.context.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(globalScreenConfig, index) => (
          <CockpitRow index={index} listLength={globalScreenConfigs.length} key={index}>
            <Text fontWeight="medium">{globalScreenConfig.name}</Text>
            <ButtonGroup marginLeft="auto" attached height="100%">
              {canEditScreenConfig && (
                <IconButton
                  variant="transparent"
                  boxSize="56px"
                  aria-label="edit"
                  onClick={() =>
                    setEditedItem({
                      tab: OperatorStationConfigurationTabEnum.CONTEXT,
                      id: globalScreenConfig.id,
                    })
                  }
                >
                  <Icon as={IcEdit} boxSize="24px" />
                </IconButton>
              )}
              {canDeleteScreenConfig && (
                <IconButton
                  variant="transparent"
                  boxSize="56px"
                  aria-label="delete"
                  onClick={() => setSelectedScreenConfiguration(globalScreenConfig)}
                >
                  <Icon as={IcTrash} boxSize="24px" />
                </IconButton>
              )}
            </ButtonGroup>
          </CockpitRow>
        )}
      </For>
    </CockpitListLayout>
  );
}
